import _ from 'lodash';
// we should remove lodash
import * as z from 'zod';

export const EMPTY_AI_LESSON_DESCRIPTION = 'AI error';

const validHexColourRegex = /^#([A-F0-9]{3})(?:[A-F0-9]{3})?$/i;

export const OverlayElementTypes = {
  Text: 'text',
  Emoji: 'emoji',
} as const;

export const CANVAS_OBJECT_BACKGROUND_TYPES = {
  Box: 'box',
  Line: 'line',
  Transparent: 'transparent',
} as const;

export const CANVAS_OBJECT_TEXT_SIZE_MAP = {
  small: 0.4,
  medium: 0.7,
  large: 1,
} as const;

//validation methods
const isString = (val: any) => typeof val === 'string';
const isNonEmptyString = (val: any) =>
  typeof val === 'string' && val.length > 0;
const isNumber = (val: any) => typeof val === 'number';
const isBoolean = (val: any) => typeof val === 'boolean';
const isArrayOfStrings = (val: any) =>
  Array.isArray(val) && _.every(val, (v: any) => typeof v === 'string');
const isNonEmptyArrayOfNonEmptyStrings = (val: any) =>
  Array.isArray(val) &&
  val.length > 0 &&
  _.every(val, (v: any) => typeof v === 'string' && v.length > 0);
const isValidOrderingType = (val: any) => ['text'].includes(val);
const isValidVideoSource = (val: any) =>
  ['cloudinary', 'youtube'].includes(val);
// 3 or 6 char hex color with # at start
const isValidHexColor = (val: any) =>
  typeof val === 'string' && validHexColourRegex.test(val);
export const isValidCustomisedButtonText = (val: any) =>
  typeof val === 'string' && val.length > 0;
const isValidCustomisedTitleText = (val: any) =>
  typeof val === 'string' && val.length > 0;
const isValidCustomisedSubtitleText = (val: any) =>
  typeof val === 'string' && val.length >= 0 && val.length <= 200;

const isValidMinTime = (val: any) =>
  val === null || (isNumber(val) && val >= 0 && val <= 120);
const isValidOverrideProgressButton = (val: any) =>
  typeof val === 'undefined' || val === null || isBoolean(val);
const isValidZoomToFit = (val: any) =>
  typeof val === 'undefined' || val === null || isBoolean(val);
const isValidIframeHeightOffset = (val: any) =>
  typeof val === 'undefined' || val === null || isNumber(val);
const isValidHideIntroSlide = (val: any) =>
  typeof val === 'undefined' || val === null || isBoolean(val);

export const isValidEmbeddedUrl = (val: any) => {
  if (!isNonEmptyString(val)) {
    return false;
  }
  if (val.includes('/lessons/customerActivityContent/')) {
    return true;
  }
  return /^(?:https:\/\/)?[^/]*(?:(figma)|(matterport)|(kaltura)|(mindstamp))[^/]*\/\S+$/.test(
    val
  );
};

const maxOrderingAnswerLength = 60;

const validHexColourSchema = z.string().regex(validHexColourRegex);

const withCustomisationsSchema = z.object({
  background: validHexColourSchema,
  fontColour: validHexColourSchema.optional(),
  isLightButtonStyle: z.boolean().optional(),
});

const IGNORE_ISSUE_IN_INCOMPLETE_ACTIVITIES =
  'IGNORE_ISSUE_IN_INCOMPLETE_ACTIVITIES';
const IGNORE_CHECK_ON_INCOMPLETE_ACTIVITIES_PARAM = {
  [IGNORE_ISSUE_IN_INCOMPLETE_ACTIVITIES]: true,
} as const;

const multipleChoiceOptionSchema = z
  .object({
    text: z.string().min(1),
    textCkey: z.string().nullish(),
    isCorrect: z.boolean(),
    tip: z.string().optional(),
    tipCkey: z.string().optional(),
    videoFeedback: z
      .object({
        url: z.string().optional(),
        urlCkey: z.string().optional(),
        title: z.string().max(30).optional(),
        titleCkey: z.string().optional(),
        fileName: z.string().optional(),
        fileNameCkey: z.string().optional(),
        source: z.string().optional(),
      })
      .optional(),
  })
  .refine(
    (opt) => {
      if (opt.tip === '') {
        return !!opt.videoFeedback?.url;
      }

      if (opt.videoFeedback?.url === '') {
        return !!opt.tip;
      }

      return true;
    },
    {params: IGNORE_CHECK_ON_INCOMPLETE_ACTIVITIES_PARAM}
  );

const multipleChoiceOptionsArraySchema = z
  .array(multipleChoiceOptionSchema)
  .refine((options) => options.some((option) => option.isCorrect), {
    params: IGNORE_CHECK_ON_INCOMPLETE_ACTIVITIES_PARAM,
  });

export const areValidOptions = (options: unknown, mandatory = true) =>
  createValidationFunctionFromSchema(multipleChoiceOptionsArraySchema)(
    options,
    !mandatory
  ).result;

type ValidatorEnv = {
  context: string[];
  reason?: string;
};

//boilerplate
const validatorWrapper = (
  validator: any,
  allowIncompleteActivities: boolean
) => {
  const env: ValidatorEnv = {
    context: [],
  };
  const validateParam = (param: any, validate: any, mandatory = true) => {
    const paramName = Object.keys(param)[0];
    if (paramName === undefined) {
      throw new Error(`paramName is undefined`);
    }
    const paramValue = param[paramName];
    const validateName = Object.keys(validate)[0];
    if (validateName === undefined) {
      throw new Error(`validateName is undefined`);
    }
    const validateFn = validate[validateName];

    if (mandatory === true) {
      if (!validateFn(paramValue, mandatory)) {
        env.reason = `Mandatory param ${paramName} failed validation criteria of ${validateName}; value was ${JSON.stringify(
          paramValue
        )}`;
        return false;
      }
      // Allow fields to be invalid while editing
    } else if (!allowIncompleteActivities) {
      if (
        typeof paramValue !== 'undefined' &&
        paramValue !== '' &&
        !validateFn(paramValue, mandatory)
      ) {
        env.reason = `Optional param ${paramName} failed validation criteria of ${validateName}; value was ${JSON.stringify(
          paramValue
        )}`;
        return false;
      }
    }
    return true;
  };

  const validateEson = (eson: any) => {
    const {overlayElements, params, type} = eson;
    env.context.push(type);
    if (!(type in validatorClosure)) {
      env.reason = `eson type '${type}' not available`;
      return false;
    }
    if (params === null || typeof params !== 'object') {
      env.reason = `params for eson of type '${type}' not supplied`;
      return false;
    }
    const result = validatorClosure[type](params, overlayElements);
    if (!env.reason) {
      env.context.pop();
    }
    return result;
  };
  const validateEsons = (esonsObject: any, atLeastOne = true) => {
    const esonsName = Object.keys(esonsObject)[0];
    if (esonsName === undefined) {
      throw new Error(`esonsName is undefined`);
    }
    const esons = esonsObject[esonsName];

    if (atLeastOne) {
      if (Array.isArray(esons) && esons.length > 0) {
        return _.every(esons, (eson: any) => validateEson(eson));
      } else {
        env.reason = `At least one of ${esonsName} need to be specified`;
        return false;
      }
    } else {
      if (!esons) {
        //allowing empty children (*)
        return true;
      }
      return (
        Array.isArray(esons) &&
        _.every(esons, (eson: any) => validateEson(eson))
      );
    }
  };

  const parseResult = (result: any) => {
    if (result === true) {
      return {
        result,
      };
    } else {
      return {
        result,
        failedReason: env.reason,
        failedEsonContext: env.context,
      };
    }
  };

  validateParam.env = env;

  const validatorClosure = validator(
    validateParam,
    validateEson,
    validateEsons
  );

  //returned function can validate any level of eson - from full lessons to smaller activities
  return (eson: any) => {
    const result = validateEson(eson);
    return parseResult(result);
  };
};

const optionalCustomisedButtonParamSchema = z
  .object({
    text: z.string().min(1),
  })
  .optional();

const validateCustomisedButtonParam = (
  validateParam: any,
  params: any,
  allowIncompleteActivities: any
) => {
  if (!params) {
    return true;
  }
  const {text} = params;
  return validateParam(
    {text},
    {isValidCustomisedButtonText},
    !allowIncompleteActivities
  );
};

const validateLearningOutcomes = (
  validateParam: any,
  params: any,
  allowIncompleteActivities: any
) => {
  if (allowIncompleteActivities && !params) {
    return true;
  }
  const {title, outcomes, cheer, button} = params;
  const testOutcomes = allowIncompleteActivities
    ? isArrayOfStrings
    : isNonEmptyArrayOfNonEmptyStrings;
  return (
    validateParam({title}, {isNonEmptyString}, !allowIncompleteActivities) &&
    validateParam({outcomes}, {testOutcomes}, !allowIncompleteActivities) &&
    validateParam({cheer}, {isNonEmptyString}, false) &&
    validateCustomisedButtonParam(
      validateParam,
      button,
      allowIncompleteActivities
    )
  );
};

const mandatoryImageParamSchema = z
  .object({
    url: z.string().min(1),
    urlCkey: z.string().optional(),
    width: z.number().optional(),
    height: z.number().optional(),
  })
  .meta({
    description:
      'Fields are only editable through the UI. Do not set any fields directly.',
  });

const imageParamWithPositionSchema = mandatoryImageParamSchema
  .safeExtend({
    position: z.enum(['top', 'bottom']),
  })
  .meta(mandatoryImageParamSchema.meta() ?? {});

const overlayElementBaseStylesSchema = z
  .object({
    x: z.number().max(9),
    y: z.number().max(16),
    scale: z.number(),
    rotate: z.number(),
  })
  .meta({
    description:
      'Positioning of the top left corner of the element in a 9:16 canvas',
  });

const emojiCodeSchema = z
  .string()
  .regex(/^[0-9a-fA-F-]+$/, {
    message:
      "Emoji code must be a valid Unicode hex code (e.g., '1f600' for 😀, '2764-fe0f' for ❤️)",
  })
  .refine(
    (code) => {
      const parts = code.split('-');
      return parts.every(
        (part) =>
          part.length >= 1 && part.length <= 6 && /^[0-9a-f]+$/.test(part)
      );
    },
    {
      message:
        "Invalid Unicode format. Use lowercase hex codes like '1f600' or '1f600-fe0f'",
    }
  )
  .meta({
    title: 'Unicode emoji code',
    description:
      'Lowercase Unicode emoji character code (e.g., "1f600" for 😀). Do NOT use literal emojis like 😀. Common examples: "1f600" (😀), "2764-fe0f" (❤️), "1f44d" (👍)',
    examples: ['1f600', '2764-fe0f', '1f44d', '1f389'],
  });

export const overlayElementContentSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal(OverlayElementTypes.Text),
    text: z.string().min(1),
    textCkey: z.string().optional(),
    backgroundType: z.enum(Object.values(CANVAS_OBJECT_BACKGROUND_TYPES)),
    backgroundColor: z.string(),
    styles: overlayElementBaseStylesSchema
      .extend({
        textColor: z.string(),
        fontSize: z.literal(Object.values(CANVAS_OBJECT_TEXT_SIZE_MAP)),
      })
      .meta(overlayElementBaseStylesSchema.meta() ?? {}),
  }),
  z.object({
    type: z.literal(OverlayElementTypes.Emoji),
    code: emojiCodeSchema,
    styles: overlayElementBaseStylesSchema,
  }),
]);

export const overlayElementSchema = z
  .array(
    overlayElementContentSchema.and(
      z.object({
        id: z.string().optional(),
        slideId: z.string().optional(),
        order: z.number(),
      })
    )
  )
  // Only allow at most one Text and one Emoji overlay element
  .superRefine((arr, ctx) => {
    let textCount = 0;
    let emojiCount = 0;
    for (let i = 0; i < arr.length; i++) {
      const el = arr[i] as z.infer<typeof overlayElementContentSchema>;
      if (el.type === OverlayElementTypes.Text) {
        textCount += 1;
      } else if (el.type === OverlayElementTypes.Emoji) {
        emojiCount += 1;
      }
    }

    if (textCount > 1) {
      ctx.addIssue({
        code: 'custom',
        message: 'Only one Text overlay element is allowed',
        path: [],
      });
    }
    if (emojiCount > 1) {
      ctx.addIssue({
        code: 'custom',
        message: 'Only one Emoji overlay element is allowed',
        path: [],
      });
    }
  })
  // Cap total items to 2 (one Text + one Emoji at most)
  .max(2, 'Only one Text and one Emoji overlay element are allowed')
  .optional();

const learningOutcomesParamsSchema = withCustomisationsSchema.extend({
  title: z.string().min(1),
  titleCkey: z.string().optional(),
  outcomes: z.array(z.string().min(1)).min(1),
  outcomesCkey: z.array(z.string().nullable()).min(1).optional(),
  cheer: z.string(),
  cheerCkey: z.string().optional(),
  button: optionalCustomisedButtonParamSchema,
});

export type V4LearningOutcomes = z.infer<typeof learningOutcomesParamsSchema>;

const validateIntro = (
  validateParam: any,
  params: any,
  allowIncompleteActivities: any
) => {
  if (allowIncompleteActivities && !params) {
    return true;
  }
  const {greeting, description, welcomeImageUrl, button} = params;
  const testString = allowIncompleteActivities
    ? {isString}
    : {isNonEmptyString};
  return (
    validateParam({welcomeImageUrl}, testString, false) &&
    validateParam({greeting}, testString, !allowIncompleteActivities) &&
    validateParam({description}, testString, !allowIncompleteActivities) &&
    validateCustomisedButtonParam(
      validateParam,
      button,
      allowIncompleteActivities
    )
  );
};

const multipleChoiceQuestionBaseSchema = z.object({
  question: z.string().min(1),
  questionCkey: z.string().optional(),
  options: multipleChoiceOptionsArraySchema,
});

const mixedQuizMultipleChoiceQuestionSchema =
  multipleChoiceQuestionBaseSchema.safeExtend({
    type: z.enum(['singleResponse', 'multipleResponse']),
  });

const MIN_ORDERING_QUESTION_OPTIONS = 3;
const MAX_ORDERING_QUESTION_OPTIONS = 5;

const orderingQuestionAnswersSchema = z
  .array(z.string().min(1).max(maxOrderingAnswerLength))
  .min(MIN_ORDERING_QUESTION_OPTIONS)
  .max(MAX_ORDERING_QUESTION_OPTIONS);

const mixedQuizOrderingQuestionOptionsSchema = z
  .array(
    z.object({
      text: z.string().min(1).max(maxOrderingAnswerLength),
      textCkey: z.string().nullish(),
    })
  )
  .min(MIN_ORDERING_QUESTION_OPTIONS)
  .max(MAX_ORDERING_QUESTION_OPTIONS);

const mixedQuizOrderingQuestionSchema = z.object({
  type: z.literal('ordering'),
  question: z.string().min(1).max(59),
  prompt: z.string().min(1).max(49),
  options: mixedQuizOrderingQuestionOptionsSchema,
});

const mixedQuizQuestionSchema = z.discriminatedUnion('type', [
  mixedQuizMultipleChoiceQuestionSchema,
  mixedQuizOrderingQuestionSchema,
]);

const validateVideo = (
  validateParam: any,
  params: any,
  allowIncompleteActivities: any
) => {
  if (!params) {
    return true;
  }
  const {url, source} = params;
  return (
    validateParam({url}, {isNonEmptyString}, !allowIncompleteActivities) &&
    validateParam({source}, {isValidVideoSource}, !allowIncompleteActivities)
  );
};

const validateOrdering = (
  validateParam: any,
  params: any,
  allowIncompleteActivities: any
) => {
  const {quizType, prompt, answers, progressButton} = params;
  const testAnswers = allowIncompleteActivities
    ? isArrayOfStrings
    : isNonEmptyArrayOfNonEmptyStrings;

  return (
    validateParam(
      {quizType},
      {isValidOrderingType},
      !allowIncompleteActivities
    ) &&
    validateParam({prompt}, {isValidOrderingPrompt}, false) &&
    validateParam({answers}, {testAnswers}, !allowIncompleteActivities) &&
    validateParam(
      {answers},
      {
        areValidAnswers: (as: any) =>
          allowIncompleteActivities || areValidOrderingAnswers(as),
      },
      !allowIncompleteActivities
    ) &&
    validateCustomisedButtonParam(
      validateParam,
      progressButton,
      allowIncompleteActivities
    )
  );
};

const baseQuizSchema = z.object({
  shuffleAnswers: z.boolean(),
  progressButton: optionalCustomisedButtonParamSchema,
});

const mixedQuizSchema = baseQuizSchema.safeExtend({
  quizType: z.literal('mixed'),
  questions: mixedQuizQuestionSchema.array(),
});

const multipleChoiceQuizSchema = baseQuizSchema.safeExtend({
  quizType: z.enum(['singleChoice', 'multipleChoice']),
  prompt: z.string().optional(),
  questions: multipleChoiceQuestionBaseSchema.array(),
});

const standaloneQuizInteractionParamsSchema = z.discriminatedUnion('quizType', [
  mixedQuizSchema,
  multipleChoiceQuizSchema,
]);

const validateStandaloneQuiz = (
  validateParam: any,
  params: any,
  allowIncompleteActivities: any
) =>
  createValidationFunctionFromSchema(standaloneQuizInteractionParamsSchema)(
    params,
    allowIncompleteActivities,
    validateParam.env
  ).result;

const imageUrlSchemaForQuiz = z
  .object({
    url: z.string().meta({
      description:
        "Used instead of 'urls' when all questions share an image or an empty string when no image is used",
    }),
    urlCkey: z.string().optional(),
    thumbnailUrl: z.string().nullish(),
    urls: z.array(z.string().nullish()).meta({
      description:
        'An array of image urls, corresponding to each question with with a matching index. An empty string can be used for questions without an image.',
    }),
    thumbnailUrls: z.array(z.string().nullish()),
  })
  .partial();

const imageQuizInteractionParamsSchema = z
  .discriminatedUnion('quizType', [
    mixedQuizSchema.safeExtend(imageUrlSchemaForQuiz.shape),
    multipleChoiceQuizSchema.safeExtend(imageUrlSchemaForQuiz.shape),
  ])
  .superRefine((data, ctx) => {
    if (!data.urls) {
      if (typeof data.url !== 'string') {
        ctx.addIssue({
          code: 'custom',
          message:
            'Either a string url or a urls array must be provided. An empty string is valid',
          continue: true,
        });
      }
      return;
    }
    if (data.urls.length !== data.questions.length) {
      ctx.addIssue({
        code: 'custom',
        message:
          'The number of urls and thumbnailUrls must match the number of questions. Use an empty string to fill the gap.',
        continue: true,
      });
    }

    if (
      data.thumbnailUrls &&
      (data.thumbnailUrls.length !== data.urls.length ||
        data.thumbnailUrls.length !== data.questions.length)
    ) {
      ctx.addIssue({
        code: 'custom',
        message:
          'The number of thumbnailUrls must match the number of urls and questions. Use an empty string to fill the gap.',
        continue: true,
      });
    }
  })
  .meta({
    description:
      'Should have either a url or urls field. Default to a url with an empty string, do not edit urls directly',
  });

const validateV3dot1ImageQuiz = (
  validateParam: any,
  params: any,
  allowIncompleteActivities: any
) =>
  createValidationFunctionFromSchema(imageQuizInteractionParamsSchema)(
    params,
    allowIncompleteActivities,
    validateParam.env
  ).result;

const baseInteractiveActivityParamsSchemaWithoutInteraction =
  withCustomisationsSchema.safeExtend({
    taskBoxTitle: z.string().optional(),
    taskBoxTitleCkey: z.string().optional(),
    taskBoxText: z.string().min(1),
    taskBoxTextCkey: z.string().optional(),
    taskBoxButton: optionalCustomisedButtonParamSchema,
  });

const validateV3dot1InteractiveActivity = (
  validateParam: any,
  validateEson: any,
  params: any,
  allowIncompleteActivities: any
) => {
  const {video, interaction} = params;
  return (
    createValidationFunctionFromSchema(
      baseInteractiveActivityParamsSchemaWithoutInteraction
    )(params, allowIncompleteActivities, validateParam.env).result &&
    validateVideo(validateParam, video, allowIncompleteActivities) &&
    validateEson(interaction)
  );
};

const validateText = (
  validateParam: any,
  params: any,
  allowIncompleteActivities: any
) => {
  const {title, description} = params;
  return (
    validateParam({title}, {isNonEmptyString}, !allowIncompleteActivities) &&
    validateParam({description}, {isNonEmptyString}, !allowIncompleteActivities)
  );
};

const baseTextSlideParamsSchema = withCustomisationsSchema.safeExtend({
  title: z.string().min(1),
  titleCkey: z.string().optional(),
  description: z.string().min(1).meta({
    description:
      'A few sentences of HTML formatted content. Ideally under 100 words.',
  }),
  descriptionCkey: z.string().optional(),
  button: optionalCustomisedButtonParamSchema,
});

const textSlideParamsSchema = baseTextSlideParamsSchema.safeExtend({
  image: imageParamWithPositionSchema.nullable(),
});

const imageSlideParamsSchema = withCustomisationsSchema.extend({
  altText: z.string().meta({
    description:
      'Text describing the image for accessibility. Do not guess the content of the image, Default to an empty string.',
  }),
  altTextCkey: z.string().optional(),
  image: mandatoryImageParamSchema,
});

export type V4Text = z.infer<typeof textSlideParamsSchema>;
export type V4ImageSlide = z.infer<typeof imageSlideParamsSchema>;

const validateV4Text = createValidationFunctionFromSchema(
  textSlideParamsSchema
);

const validateV4ImageSlide = createValidationFunctionFromSchema(
  imageSlideParamsSchema
);

const validateV4ImageSlideOverlayElements =
  createValidationFunctionFromSchema(overlayElementSchema);

// eslint-disable-next-line complexity
const validateV4ThirdParty = (
  validateParam: any,
  params: any,
  allowIncompleteActivities: boolean
) => {
  const {
    url,
    title,
    description,
    background,
    fontColour,
    button,
    introButton,
    minTime,
    overrideProgressButton,
    zoomToFit,
    iframeHeightOffset,
    hideIntroSlide,
  } = params;
  return (
    validateParam({url}, {isValidEmbeddedUrl}, !allowIncompleteActivities) &&
    validateParam({title}, {isNonEmptyString}, false) &&
    validateParam(
      {description},
      {isNonEmptyString},
      !allowIncompleteActivities
    ) &&
    validateParam(
      {background},
      {isValidHexColor},
      !allowIncompleteActivities
    ) &&
    validateParam({fontColour}, {isValidHexColor}, false) &&
    validateParam({minTime}, {isValidMinTime}, !allowIncompleteActivities) &&
    validateParam(
      {overrideProgressButton},
      {isValidOverrideProgressButton},
      !allowIncompleteActivities
    ) &&
    validateParam(
      {zoomToFit},
      {isValidZoomToFit},
      !allowIncompleteActivities
    ) &&
    validateParam(
      {iframeHeightOffset},
      {isValidIframeHeightOffset},
      !allowIncompleteActivities
    ) &&
    validateParam(
      {hideIntroSlide},
      {isValidHideIntroSlide},
      !allowIncompleteActivities
    ) &&
    validateCustomisedButtonParam(
      validateParam,
      button,
      allowIncompleteActivities
    ) &&
    validateCustomisedButtonParam(
      validateParam,
      introButton,
      allowIncompleteActivities
    )
  );
};

const validateImage = (
  validateParam: any,
  params: any,
  allowIncompleteActivities: any
) => {
  const {url, caption, width, height, button} = params;
  return (
    validateParam({url}, {isNonEmptyString}, !allowIncompleteActivities) &&
    validateParam({caption}, {isNonEmptyString}, false) &&
    validateParam({width}, {isNumber}, false) &&
    validateParam({height}, {isNumber}, false) &&
    validateCustomisedButtonParam(
      validateParam,
      button,
      allowIncompleteActivities
    )
  );
};

const validateVideoActivity = (
  validateParam: any,
  params: any,
  allowIncompleteActivities: any
) => {
  const {url, source, caption, button} = params;
  return (
    validateParam({url}, {isNonEmptyString}, !allowIncompleteActivities) &&
    validateParam({source}, {isValidVideoSource}, !allowIncompleteActivities) &&
    validateParam({caption}, {isNonEmptyString}, false) &&
    validateCustomisedButtonParam(
      validateParam,
      button,
      allowIncompleteActivities
    )
  );
};

const resultsSchema = z
  .object({
    minimumScore: z.undefined(),
    message: z.string(),
  })
  .or(
    z.object({
      minimumScore: z.number().min(0).max(100).multipleOf(5),
      passMessage: z.string(),
      failMessage: z.string(),
    })
  );

const videoParamsSchema = z
  .object({
    url: z.string().min(1),
    urlCkey: z.string().optional(),
    source: z
      .enum(['cloudinary', 'youtube'])
      .optional()
      .refine((source) => !!source, {
        params: IGNORE_CHECK_ON_INCOMPLETE_ACTIVITIES_PARAM,
      }),
  })
  .meta({
    description:
      'Fields are only editable through the UI. Do not set directly.',
  });

const validateV4Video = (
  validateParam: any,
  params: any,
  allowIncompleteActivities: any
) =>
  createValidationFunctionFromSchema(videoParamsSchema)(
    params,
    allowIncompleteActivities,
    validateParam.env
  ).result;

const endSlideParamsBaseSchema = withCustomisationsSchema.extend({
  title: z.string().nullable(),
  titleCkey: z.string().optional(),
  subtitle: z.string().nullable(),
  subtitleCkey: z.string().optional(),
  icon: z.object({url: z.string(), urlCkey: z.string().optional()}),
});

const customisedEndSlideParamsSchema = endSlideParamsBaseSchema.extend({
  title: z.string().min(1),
  subtitle: z.string().max(200).optional(),
  button: optionalCustomisedButtonParamSchema.unwrap(),
});

export const validateV3dot1CustomisedEndOfActivityScreen = (
  validateParam: any,
  params: any,
  allowIncompleteActivities: any,
  endOfActivityEnabled = true
) => {
  if (!endOfActivityEnabled) {
    return true;
  }

  const {title, subtitle, button, icon, endOfActivityCustomTextOptions} =
    params;

  if (endOfActivityCustomTextOptions) {
    return (
      validateParam(
        {button: button?.text},
        {isValidCustomisedButtonText},
        !allowIncompleteActivities
      ) &&
      validateParam(
        {title},
        {isValidCustomisedTitleText},
        !allowIncompleteActivities
      ) &&
      validateParam({subtitle}, {isValidCustomisedSubtitleText}, false) &&
      validateParam({icon: icon?.url}, {isNonEmptyString}, false)
    );
  } else {
    return validateParam({icon: icon?.url}, {isNonEmptyString}, false);
  }
};

const v3dot1Validator =
  (allowIncompleteActivities: any, endOfActivityEnabled: any) =>
  // @ts-expect-error TS(7006): Parameter 'validateParam' implicitly has an 'any' ... Remove this comment to see the full error message
  (validateParam, validateEson, validateEsons) => ({
    // eslint-disable-next-line complexity
    lesson: (params: any) => {
      const {
        title,
        previewImageUrl,
        completionMessage,
        intro,
        learningOutcomes,
        activities,
        end,
        endOfActivityCustomTextOptions,
      } = params;
      return (
        validateParam(
          {title},
          {isNonEmptyString},
          !allowIncompleteActivities
        ) &&
        validateParam({completionMessage}, {isString}, false) &&
        validateParam({previewImageUrl}, {isString}, false) &&
        (!intro || validateEson({type: 'intro', params: intro})) &&
        (!learningOutcomes ||
          validateEson({type: 'learningOutcomes', params: learningOutcomes})) &&
        validateEsons({activities}, !allowIncompleteActivities) &&
        (!end ||
          validateEson({
            type: 'end',
            params: {...end, endOfActivityCustomTextOptions},
          }))
      );
    },

    intro: (params: any) =>
      _.isEmpty(params) ||
      !params ||
      validateIntro(validateParam, params, allowIncompleteActivities),

    learningOutcomes: (params: any) =>
      _.isEmpty(params) ||
      !params ||
      validateLearningOutcomes(
        validateParam,
        params,
        allowIncompleteActivities
      ),

    end: (params: any) =>
      _.isEmpty(params) ||
      !params ||
      validateV3dot1CustomisedEndOfActivityScreen(
        validateParam,
        params,
        allowIncompleteActivities,
        endOfActivityEnabled
      ),

    text: (params: any) =>
      (allowIncompleteActivities && _.isEmpty(params)) ||
      validateText(validateParam, params, allowIncompleteActivities),

    image: (params: any) =>
      (allowIncompleteActivities && _.isEmpty(params)) ||
      validateImage(validateParam, params, allowIncompleteActivities),

    video: (params: any) =>
      (allowIncompleteActivities && _.isEmpty(params)) ||
      validateVideoActivity(validateParam, params, allowIncompleteActivities),

    interactive: (params: any) =>
      (allowIncompleteActivities && _.isEmpty(params)) ||
      validateV3dot1InteractiveActivity(
        validateParam,
        validateEson,
        params,
        allowIncompleteActivities
      ),

    standaloneQuiz: (params: any) =>
      (allowIncompleteActivities && _.isEmpty(params)) ||
      validateStandaloneQuiz(validateParam, params, allowIncompleteActivities),

    imageQuiz: (params: any) =>
      (allowIncompleteActivities && _.isEmpty(params)) ||
      validateV3dot1ImageQuiz(validateParam, params, allowIncompleteActivities),

    ordering: (params: any) =>
      (allowIncompleteActivities && _.isEmpty(params)) ||
      validateOrdering(validateParam, params, allowIncompleteActivities),
  });

const v4Validator =
  (allowIncompleteActivities: boolean, endOfActivityEnabled: boolean) =>
  // @ts-expect-error TS(7006): Parameter 'validateParam' implicitly has an 'any' ... Remove this comment to see the full error message
  (validateParam, validateEson, validateEsons) => ({
    // eslint-disable-next-line complexity
    lesson: (params: any) => {
      const {
        title,
        previewImageUrl,
        completionMessage,
        intro,
        learningOutcomes,
        activities,
        end,
        endOfActivityCustomTextOptions,
      } = params;
      return (
        validateParam(
          {title},
          {isNonEmptyString},
          !allowIncompleteActivities
        ) &&
        validateParam({completionMessage}, {isString}, false) &&
        validateParam({previewImageUrl}, {isString}, false) &&
        (!intro ||
          validateIntroSlide(
            intro,
            allowIncompleteActivities,
            validateParam.env
          ).result) &&
        (!learningOutcomes ||
          validateLearningOutcomesSlide(
            learningOutcomes,
            allowIncompleteActivities,
            validateParam.env
          ).result) &&
        validateEsons({activities}, !allowIncompleteActivities) &&
        (!end ||
          !endOfActivityEnabled ||
          validateEndSlideByCustomisationSetting(
            endOfActivityCustomTextOptions
          )(end, allowIncompleteActivities, validateParam.env).result)
      );
    },

    text: (params: unknown) =>
      (allowIncompleteActivities && _.isEmpty(params)) ||
      validateV4Text(params, allowIncompleteActivities, validateParam.env)
        .result,

    imageSlide: (params: any, overlayElements: any) =>
      (allowIncompleteActivities && _.isEmpty(params)) ||
      (validateV4ImageSlide(
        params,
        allowIncompleteActivities,
        validateParam.env
      ).result &&
        validateV4ImageSlideOverlayElements(
          overlayElements,
          allowIncompleteActivities,
          validateParam.env
        ).result),

    thirdParty: (params: any) =>
      (allowIncompleteActivities && _.isEmpty(params)) ||
      validateV4ThirdParty(validateParam, params, allowIncompleteActivities),

    video: (params: any) =>
      (allowIncompleteActivities && _.isEmpty(params)) ||
      validateV4Video(validateParam, params, allowIncompleteActivities),

    interactive: (params: any) =>
      (allowIncompleteActivities && _.isEmpty(params)) ||
      validateV3dot1InteractiveActivity(
        validateParam,
        validateEson,
        params,
        allowIncompleteActivities
      ),

    standaloneQuiz: (params: any) =>
      (allowIncompleteActivities && _.isEmpty(params)) ||
      validateStandaloneQuiz(validateParam, params, allowIncompleteActivities),

    imageQuiz: (params: any) =>
      (allowIncompleteActivities && _.isEmpty(params)) ||
      validateV3dot1ImageQuiz(validateParam, params, allowIncompleteActivities),

    ordering: (params: any) =>
      (allowIncompleteActivities && _.isEmpty(params)) ||
      validateOrdering(validateParam, params, allowIncompleteActivities),
  });

export const v4AssessmentValidator =
  (allowIncompleteActivities: boolean) =>
  (validateParam: any, validateEson: any, validateEsons: any) => ({
    assessment: (params: any) => {
      const {title, previewImageUrl, intro, activities, results, minimumScore} =
        params;
      return (
        validateParam(
          {title},
          {isNonEmptyString},
          !allowIncompleteActivities
        ) &&
        validateParam({previewImageUrl}, {isString}, false) &&
        validateParam({minimumScore}, {isNumber}, false) &&
        (!intro ||
          validateIntroSlide(
            intro,
            allowIncompleteActivities,
            validateParam.env
          ).result) &&
        validateEsons({activities}, !allowIncompleteActivities) &&
        (!results ||
          validateResultsSlide(results, allowIncompleteActivities, minimumScore)
            .result) &&
        validateCustomisedButtonParam(
          validateParam,
          intro.button,
          allowIncompleteActivities
        ) &&
        validateCustomisedButtonParam(
          validateParam,
          activities[0].params.interaction.params.progressButton,
          allowIncompleteActivities
        )
      );
    },

    interactive: (params: any) =>
      (allowIncompleteActivities && _.isEmpty(params)) ||
      validateV3dot1InteractiveActivity(
        validateParam,
        validateEson,
        params,
        allowIncompleteActivities
      ),

    standaloneQuiz: (params: any) =>
      (allowIncompleteActivities && _.isEmpty(params)) ||
      validateStandaloneQuiz(validateParam, params, allowIncompleteActivities),

    imageQuiz: (params: any) =>
      (allowIncompleteActivities && _.isEmpty(params)) ||
      validateV3dot1ImageQuiz(validateParam, params, allowIncompleteActivities),

    ordering: (params: any) =>
      (allowIncompleteActivities && _.isEmpty(params)) ||
      validateOrdering(validateParam, params, allowIncompleteActivities),
  });

//<Lesson> → {result: <Boolean>, failedReason?: <string>, failedEsonContext?: <[<string>]>}
export const validateLesson = (
  lesson: any,
  allowIncompleteActivities: any,
  endOfActivityEnabled?: any
) => {
  switch (lesson.version) {
    case 4: {
      const validator = validatorWrapper(
        v4Validator(allowIncompleteActivities, endOfActivityEnabled),
        allowIncompleteActivities
      );
      return validator(lesson);
    }
    case 3.1: {
      const validator = validatorWrapper(
        v3dot1Validator(allowIncompleteActivities, endOfActivityEnabled),
        allowIncompleteActivities
      );
      return validator(lesson);
    }
    default: {
      return {
        result: false,
        failedReason: `Invalid lesson version given: ${lesson.version}`,
      };
    }
  }
};

export const validateAssessment = (
  assessment: any,
  allowIncompleteActivities?: any
) => {
  switch (assessment.version) {
    case 4: {
      const validator = validatorWrapper(
        v4AssessmentValidator(allowIncompleteActivities),
        allowIncompleteActivities
      );
      return validator(assessment);
    }
    default: {
      return {
        result: false,
        failedReason: `Invalid assessment version given: ${assessment.version}`,
      };
    }
  }
};

export const validateLessonOrAssessment = (
  content: any,
  allowIncompleteActivities: any,
  endOfActivityEnabled: any
) => {
  const validationFunc =
    content.type === 'assessment' ? validateAssessment : validateLesson;
  return validationFunc(
    content,
    allowIncompleteActivities,
    endOfActivityEnabled
  );
};

// Avoid filtering out ALL outcomes
const filterOutcomes = (outcome: any, i: any, hasAnyOutcomes: any) =>
  (i === 0 && !hasAnyOutcomes) || !!outcome;
const filterOptions = (option: any, i: any) => i < 2 || !!option.text;

export const removeEmptyAnswersFromV3Activity = (activity: any) => {
  if (
    activity.type === 'interactive' &&
    activity.params.interaction?.params.questions
  ) {
    const isNonEmptyQuestion = (question: any) =>
      question.options.some((option: any) => !!option.text) ||
      !!question.question;

    const indexesForRemoval =
      activity.params.interaction.params.questions.reduce(
        (prevVal: any, currVal: any, currIndex: any) => {
          if (!isNonEmptyQuestion(currVal)) {
            prevVal.push(currIndex);
          }
          return prevVal;
        },
        []
      );

    if (
      indexesForRemoval.length ===
      activity.params.interaction.params.questions.length
    ) {
      indexesForRemoval.shift();
    }

    const paramsKeysToFilterByIndex = [
      'questions',
      'urls',
      'urlsCkey',
      'thumbnailUrls',
      'thumbnailUrlsCkey',
    ];
    const isIndexToKeep = (_val: any, index: any) =>
      !indexesForRemoval.includes(index);

    paramsKeysToFilterByIndex.forEach((key) => {
      if (activity.params.interaction.params[key]) {
        activity.params.interaction.params[key] =
          activity.params.interaction.params[key].filter(isIndexToKeep);
      }
    });

    activity.params.interaction.params.questions.forEach(
      (question: any, index: number) => {
        if (question.options) {
          question.options = question.options.filter(filterOptions);
        }
        if (question.type === 'ordering') {
          question.options = question.options.map((option: any) => {
            delete option.isCorrect;
            delete option.tip;
            delete option.videoFeedback;
            return option;
          });
          if (activity.params.interaction.params.urls?.[index]) {
            activity.params.interaction.params.urls[index] = '';
          }
          if (activity.params.interaction.params.thumbnailUrls?.[index]) {
            activity.params.interaction.params.thumbnailUrls[index] = '';
          }
        }
      }
    );
  }
  return activity;
};

export const removeEmptyAnswersFromV4Activity =
  removeEmptyAnswersFromV3Activity;

export const removeEmptyV3Outcomes = (learningOutcomes: any) => {
  if (!learningOutcomes.outcomes) {
    return learningOutcomes;
  }
  const hasAnyOutcomes = learningOutcomes.outcomes.some((o: any) => !!o);
  if (learningOutcomes.outcomesCkey) {
    const outcomesWithCkeys = learningOutcomes.outcomes.map(
      (o: any, i: any) => [o, learningOutcomes.outcomesCkey[i]]
    );
    // @ts-expect-error TS(7031): Binding element 'o' implicitly has an 'any' type.
    const filteredOutcomes = outcomesWithCkeys.filter(([o], i: any) =>
      filterOutcomes(o, i, hasAnyOutcomes)
    );
    // @ts-expect-error TS(7031): Binding element 'o' implicitly has an 'any' type.
    learningOutcomes.outcomes = filteredOutcomes.map(([o]) => o);
    // @ts-expect-error TS(7031): Binding element 'cKey' implicitly has an 'any' typ... Remove this comment to see the full error message
    learningOutcomes.outcomesCkey = filteredOutcomes.map(([, cKey]) => cKey);
    return learningOutcomes;
  }
  learningOutcomes.outcomes = learningOutcomes.outcomes.filter(
    (o: any, i: any) => filterOutcomes(o, i, hasAnyOutcomes)
  );
  return learningOutcomes;
};

export const removeEmptyV4Outcomes = removeEmptyV3Outcomes;

export const validateV3dot1Activity = (
  activityEson: any,
  allowIncompleteActivities: any
) => {
  // @ts-expect-error
  const validator = validatorWrapper(
    // @ts-expect-error TS(2554): Expected 2 arguments, but got 1.
    v3dot1Validator(allowIncompleteActivities)
  );
  return validator(activityEson);
};

export const validateV4Activity = (
  activityEson: any,
  allowIncompleteActivities: any
) => {
  // @ts-expect-error TS(2554): Expected 2 arguments, but got 1.
  const validator = validatorWrapper(v4Validator(allowIncompleteActivities));
  return validator(activityEson);
};

export const validateV3Intro = (intro: any, allowIncompleteActivities: any) => {
  // @ts-expect-error
  const validator = validatorWrapper(
    // @ts-expect-error TS(2554): Expected 2 arguments, but got 1.
    v3dot1Validator(allowIncompleteActivities)
  );
  return validator({type: 'intro', params: intro});
};

export const validateV3LearningOutcomes = (
  learningOutcomes: any,
  allowIncompleteActivities: any
) => {
  // @ts-expect-error
  const validator = validatorWrapper(
    // @ts-expect-error TS(2554): Expected 2 arguments, but got 1.
    v3dot1Validator(allowIncompleteActivities)
  );
  return validator({type: 'learningOutcomes', params: learningOutcomes});
};

function createValidationFunctionFromSchema<T extends z.ZodTypeAny>(schema: T) {
  return (
    data: unknown,
    allowIncompleteActivities: boolean,
    env?: ValidatorEnv
  ) =>
    allowIncompleteActivities
      ? isIncompleteSchemaDataValid(schema, env)(data)
      : isSchemaDataValid(schema, env)(data);
}

const introSlideParamsSchema = baseTextSlideParamsSchema.safeExtend({
  image: imageParamWithPositionSchema.nullish(),
});

export type V4Intro = z.infer<typeof introSlideParamsSchema>;
export const validateIntroSlide = createValidationFunctionFromSchema(
  introSlideParamsSchema
);

export const validateEndSlideByCustomisationSetting = (
  endOfActivityCustomTextOptions: boolean
) => {
  if (endOfActivityCustomTextOptions) {
    return createValidationFunctionFromSchema(customisedEndSlideParamsSchema);
  }
  return createValidationFunctionFromSchema(endSlideParamsBaseSchema);
};

export const validateEndBubble = (end: any, allowIncompleteActivities: any) => {
  // @ts-expect-error
  const validator = validatorWrapper(
    // @ts-expect-error TS(2554): Expected 2 arguments, but got 1.
    v3dot1Validator(allowIncompleteActivities)
  );
  return validator({type: 'end', params: end});
};

export const validateLearningOutcomesSlide = createValidationFunctionFromSchema(
  learningOutcomesParamsSchema
);

export const validateResultsSlide = (
  results: object,
  allowIncompleteActivities: boolean,
  minimumScore?: number
) =>
  createValidationFunctionFromSchema(resultsSchema)(
    {...results, minimumScore},
    allowIncompleteActivities
  );

export const isValidTaskBox = (params: any) => {
  const {taskBoxTitle, taskBoxText} = params;
  return isNonEmptyString(taskBoxTitle) && isNonEmptyString(taskBoxText);
};

export const isValidVideo = (params: any) => {
  const {url, source} = params;
  return isNonEmptyString(url) && isValidVideoSource(source);
};

export const isValidQuestion = (params: any) => {
  const {question, prompt, options, type} = params;
  if (type === 'ordering') {
    return (
      isValidMixedQuizOrderingQuestion(question) &&
      isValidMixedQuizOrderingPrompt(prompt) &&
      areValidMixedQuizOrderingOptions(options)
    );
  }
  return isNonEmptyString(question) && areValidOptions(options);
};

export const isValidOrderingPrompt = (val: any) =>
  isNonEmptyString(val) && val.length <= 75;

export const isValidMixedQuizOrderingPrompt = (val: string): boolean =>
  mixedQuizOrderingQuestionSchema.shape.prompt.safeParse(val).success;

export const isValidMixedQuizOrderingQuestion = (val: string): boolean =>
  mixedQuizOrderingQuestionSchema.shape.question.safeParse(val).success;

export const areValidOrderingAnswers = (answers: any) =>
  orderingQuestionAnswersSchema.safeParse(answers).success;

const areValidMixedQuizOrderingOptions = (options: {text: string}[]) =>
  mixedQuizOrderingQuestionOptionsSchema.safeParse(options).success;

export const isValidOrderingTask = (params: any) => {
  const {prompt, answers} = params;
  return isValidOrderingPrompt(prompt) && areValidOrderingAnswers(answers);
};

const addZodIssueToEnv = (env: ValidatorEnv, issues: z.core.$ZodIssue[]) => {
  if (issues[0]) {
    env.reason = issues[0].message;
    env.context = [issues[0].path.join('.')];
  }
};

function isSchemaDataValid<T extends z.ZodTypeAny>(
  schema: T,
  env?: ValidatorEnv
) {
  return (data: unknown) => {
    const res = schema.safeParse(data);
    if (env && !res.success) {
      addZodIssueToEnv(env, res.error.issues);
    }
    return convertZodReturnToLegacyFormat(res);
  };
}

type NonMandatoryZodIssues =
  | z.core.$ZodIssueTooSmall
  | z.core.$ZodIssueTooBig
  | z.core.$ZodIssueInvalidStringFormat;

const nonMandatoryIssueCodes: z.core.$ZodIssueCode[] = [
  'too_small',
  'too_big',
  'invalid_format',
] satisfies NonMandatoryZodIssues['code'][];

export const isIssueCodeNonMandatory = (
  issue: z.core.$ZodRawIssue | z.core.$ZodIssue
) =>
  nonMandatoryIssueCodes.includes(issue.code) ||
  (issue.code === 'invalid_type' && issue.input === undefined) ||
  (issue.code === 'custom' &&
    issue.params?.[IGNORE_ISSUE_IN_INCOMPLETE_ACTIVITIES]);

export function isIncompleteSchemaDataValid<T extends z.ZodTypeAny>(
  schema: T,
  env?: ValidatorEnv
) {
  return (data: unknown) => {
    const result = schema.safeParse(data, {reportInput: true});

    if (!result.success) {
      const mandatoryIssues = result.error.issues.filter(
        (i) => !isIssueCodeNonMandatory(i)
      );
      const hasNoMandatoryIssues = mandatoryIssues.length === 0;

      if (hasNoMandatoryIssues) {
        return {
          result: true,
        };
      }

      if (env) {
        addZodIssueToEnv(env, mandatoryIssues);
      }
    }

    return convertZodReturnToLegacyFormat(result);
  };
}
function convertZodReturnToLegacyFormat<
  T extends z.ZodSafeParseResult<unknown>,
>(result: T) {
  const {success, ...resultRest} = result;
  return {
    result: success,
    ...resultRest,
  };
}

const introSlideSchema = z.object({
  type: z.literal('intro'),
  params: introSlideParamsSchema,
});

const learningOutcomesSlideSchema = z.object({
  type: z.literal('learningOutcomes'),
  params: learningOutcomesParamsSchema,
});

const textActivitySlideSchema = z.object({
  type: z.literal('text'),
  params: textSlideParamsSchema,
});

const imageSlideSchema = z.object({
  type: z.literal('imageSlide'),
  params: imageSlideParamsSchema,
  overlayElements: overlayElementSchema.optional(),
});

const videoSlideSchema = z.object({
  type: z.literal('video'),
  params: videoParamsSchema,
});

const imageQuizInteractionSchema = z.object({
  type: z.literal('imageQuiz'),
  params: imageQuizInteractionParamsSchema,
});

const standaloneQuizInteractionSchema = z.object({
  type: z.literal('standaloneQuiz'),
  params: standaloneQuizInteractionParamsSchema,
});

const standaloneQuizInteractiveActivityParamsSchema =
  baseInteractiveActivityParamsSchemaWithoutInteraction
    .safeExtend({
      interaction: standaloneQuizInteractionSchema,
      video: videoParamsSchema.optional().meta(videoParamsSchema.meta() ?? {}),
    })
    .meta({description: 'A video quiz'});

const imageQuizInteractiveActivityParamsSchema =
  baseInteractiveActivityParamsSchemaWithoutInteraction
    .safeExtend({
      interaction: imageQuizInteractionSchema,
    })
    .meta({description: 'A quiz that can optionally include images.'});

// Ordering interaction not yet included in schema
const supportedInteractiveSlideSchema = z.object({
  type: z.literal('interactive'),
  params: imageQuizInteractiveActivityParamsSchema
    .or(standaloneQuizInteractiveActivityParamsSchema)
    .meta({description: 'Should default to an imageQuiz unless'}),
});

const endSlideSchema = z.object({
  type: z.literal('end'),
  params: endSlideParamsBaseSchema.or(
    customisedEndSlideParamsSchema.safeExtend({
      endOfActivityCustomTextOptions: z.literal(true),
    })
  ),
});

// TODO: Add thirdParty slide type
const editorSlidesSchema = z.discriminatedUnion('type', [
  introSlideSchema,
  learningOutcomesSlideSchema,
  supportedInteractiveSlideSchema,
  textActivitySlideSchema,
  imageSlideSchema,
  videoSlideSchema,
  endSlideSchema,
]);

export const editorSlidesArraySchema = z
  .array(editorSlidesSchema)
  .refine(
    (arr) => {
      const slideTypes = arr
        .map(({type}) => type)
        .filter((type) => ['intro', 'learningOutcomes', 'end'].includes(type));
      const uniqueSlideTypes = new Set(slideTypes);
      return slideTypes.length === uniqueSlideTypes.size;
    },
    {
      message:
        'There can not be more than one intro, learningOutcomes and end slide each',
    }
  )
  .refine(
    (arr) => arr.every((slide, index) => slide.type !== 'intro' || index === 0),
    {message: 'An intro slide must be the first slide if it exists'}
  )
  .refine(
    (arr) => {
      const learningOutcomesIndex = arr.findIndex(
        (s) => s.type === 'learningOutcomes'
      );

      if (learningOutcomesIndex === -1 || learningOutcomesIndex === 0) {
        return true;
      }

      return learningOutcomesIndex === 1 && arr[0]?.type === 'intro';
    },
    {
      message:
        'Only a single intro slide can come before a learningOutcomes slide',
    }
  )
  .refine(
    (arr) =>
      arr.every(
        (slide, index) => slide.type !== 'end' || index === arr.length - 1
      ),
    {message: 'An end slide must be the last slide'}
  );

export const DEFAULT_BACKGROUND_COLOURS = [
  '#FFFFFF',
  '#0D003A',
  '#D0F4E6',
  '#C7D9BD',
  '#57D6C8',
  '#A5CBE0',
  '#F7E8A9',
  '#EBBAA5',
  '#FF9D9A',
  '#F8B3E3',
];
